import { SortableHeader } from '../SortableHeader';
import { formatCurrency, formatTime } from './SharedComponents';
import { Loader2 } from 'lucide-react';

const getStatusBadge = (invoice) => {
    // If verification is in progress
    if (invoice.verification_status === 'pending' || invoice.verification_status === 'processing') {
        return (
            <span className="badge bg-sky-100 text-sky-700 flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" />
                Verificando...
            </span>
        );
    }
    
    // If confirmed
    if (invoice.confirmed) {
        return <span className="badge badge-success">Confirmado</span>;
    }
    
    // Pending
    return <span className="badge badge-pending">Pendiente</span>;
};

export const InvoiceTableRow = ({ invoice, showUserColumn = true }) => (
    <tr key={invoice.id} data-testid={`invoice-row-${invoice.id}`}>
        <td className="font-medium text-slate-700">
            {invoice.name}
        </td>
        <td className="text-slate-500 max-w-xs truncate">
            {invoice.description || <span className="italic text-slate-300">Sin descripción</span>}
        </td>
        {showUserColumn && (
            <td>
                <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-sky-100 flex items-center justify-center">
                        <span className="text-sky-600 text-xs font-semibold">
                            {invoice.user_name?.charAt(0).toUpperCase()}
                        </span>
                    </div>
                    <span className="text-slate-600">{invoice.user_name}</span>
                </div>
            </td>
        )}
        <td className="text-slate-500 text-sm">
            {formatTime(invoice.created_at)}
        </td>
        <td className="font-semibold text-slate-800">
            {formatCurrency(invoice.amount)}
        </td>
        <td>
            {invoice.category ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-slate-100 text-slate-700">
                    {invoice.category}
                </span>
            ) : (
                <span className="text-xs text-slate-400">—</span>
            )}
        </td>
        <td>
            {getStatusBadge(invoice)}
        </td>
    </tr>
);

export const InvoiceTableHeader = ({ sortKey, sortDirection, onSort, showUserColumn = true }) => (
    <thead>
        <tr>
            <SortableHeader 
                label="Nombre" 
                sortKey="name" 
                currentSort={sortKey} 
                currentDirection={sortDirection} 
                onSort={onSort} 
            />
            <SortableHeader 
                label="Descripción" 
                sortKey="description" 
                currentSort={sortKey} 
                currentDirection={sortDirection} 
                onSort={onSort} 
            />
            {showUserColumn && (
                <SortableHeader 
                    label="Usuario" 
                    sortKey="user_name" 
                    currentSort={sortKey} 
                    currentDirection={sortDirection} 
                    onSort={onSort} 
                />
            )}
            <SortableHeader 
                label="Hora" 
                sortKey="created_at" 
                currentSort={sortKey} 
                currentDirection={sortDirection} 
                onSort={onSort} 
            />
            <SortableHeader 
                label="Monto" 
                sortKey="amount" 
                currentSort={sortKey} 
                currentDirection={sortDirection} 
                onSort={onSort} 
            />
            <th className="w-16">Cat.</th>
            <SortableHeader 
                label="Estado" 
                sortKey="confirmed" 
                currentSort={sortKey} 
                currentDirection={sortDirection} 
                onSort={onSort} 
            />
        </tr>
    </thead>
);

export const SimpleInvoiceTable = ({ 
    invoices, 
    sortKey, 
    sortDirection, 
    onSort, 
    showUserColumn = true 
}) => (
    <div className="overflow-x-auto">
        <table className="data-table table-zebra">
            <InvoiceTableHeader 
                sortKey={sortKey} 
                sortDirection={sortDirection} 
                onSort={onSort}
                showUserColumn={showUserColumn}
            />
            <tbody>
                {invoices.map((invoice) => (
                    <InvoiceTableRow 
                        key={invoice.id} 
                        invoice={invoice}
                        showUserColumn={showUserColumn}
                    />
                ))}
            </tbody>
        </table>
    </div>
);
